// A retail park's pro forma: what it costs to build and what the rent roll
// is worth.
//
// A retail park is let, not sold by the square metre, so the value is the
// income capitalised at a yield: rent by unit type, less vacancy and the
// costs the landlord cannot pass on, is the net operating income, and NOI
// over the exit yield is what an investor pays. The figure developers watch
// is the yield on cost, NOI over everything spent: a scheme is worth doing
// when it clears the exit yield by a margin.
//
// The defaults are placeholders of the right order of magnitude for
// Romania and nothing more; the page says so.
import type { RetailGenerated } from "./generate.ts";
import type { RetailKind } from "./programme.ts";

export interface RetailFinanceInputs {
  /** The plot, all in, €. */
  land: number;
  /** Shell and core of the boxes, € / m² built. */
  costShell: number;
  /** A pad (restaurant shell), € / m² built. */
  costPad: number;
  /** Car park, roads, yards, landscaping, utilities: € / m² of plot outside the buildings. */
  costExternal: number;
  /** Design, permits, fees, financing: share of the hard cost. */
  softPct: number;
  contingencyPct: number;
  /** Headline rent, € / m² GLA / month, by unit type. */
  rent: Record<RetailKind | "pad", number>;
  /** Share of the rent lost to empty units over a cycle. */
  vacancyPct: number;
  /** Costs the service charge does not recover, share of the rent. */
  nonRecoverablePct: number;
  /** Rent-free months given at first letting. */
  rentFreeMonths: number;
  /** Fit-out contribution to tenants, € / m² GLA. */
  fitOut: number;
  /** Letting agents, share of the first year's rent. */
  lettingPct: number;
  /** Yield an investor buys the let park at, %. */
  exitYield: number;
  /** Purchaser's costs, share of the gross value. */
  purchaserPct: number;
}

/** Placeholders: retail parks in Romanian county towns, autumn 2026, order of magnitude only. */
export const DEFAULT_RETAIL_FINANCE: RetailFinanceInputs = {
  land: 0,
  costShell: 600,
  costPad: 900,
  costExternal: 45,
  softPct: 10,
  contingencyPct: 5,
  rent: { anchor: 6.5, medium: 8.5, small: 12, pad: 18 },
  vacancyPct: 3,
  nonRecoverablePct: 3,
  rentFreeMonths: 3,
  fitOut: 40,
  lettingPct: 10,
  exitYield: 7.75,
  purchaserPct: 2.5,
};

export interface RetailProForma {
  /** Headline rent a year, €, and by type. */
  rentRoll: number;
  byKind: { kind: RetailKind | "pad"; gla: number; rent: number }[];
  noi: number;
  /** NOI over the exit yield. */
  gdv: number;
  /** GDV less purchaser's costs: what the seller receives. */
  netValue: number;
  cost: { land: number; shell: number; pads: number; external: number; soft: number; contingency: number; letting: number; incentives: number; total: number };
  profit: number;
  /** Profit over net value. */
  margin: number;
  onCost: number;
  /** NOI over total cost, %: the developer's first figure. */
  yieldOnCost: number;
  /** Cost per m² GLA, all in. */
  costPerGla: number;
}

export function retailProForma(g: RetailGenerated, f: RetailFinanceInputs): RetailProForma {
  const s = g.scheme;
  const kinds: (RetailKind | "pad")[] = ["anchor", "medium", "small", "pad"];
  const byKind = kinds.map((kind) => {
    const gla = kind === "pad" ? s.pads.reduce((t, p) => t + p.gla, 0) : s.units.filter((u) => u.kind === kind).reduce((t, u) => t + u.gla, 0);
    return { kind, gla, rent: gla * (f.rent[kind] ?? 0) * 12 };
  }).filter((x) => x.gla > 0);
  const rentRoll = byKind.reduce((t, x) => t + x.rent, 0);
  const noi = rentRoll * (1 - f.vacancyPct / 100) * (1 - f.nonRecoverablePct / 100);
  const gdv = f.exitYield > 0 ? noi / (f.exitYield / 100) : 0;
  const netValue = gdv * (1 - f.purchaserPct / 100);

  const shell = s.units.reduce((t, u) => t + u.area, 0) * f.costShell;
  const pads = s.pads.reduce((t, p) => t + p.area, 0) * f.costPad;
  const external = Math.max(0, g.balance.plot - g.balance.built) * f.costExternal;
  const hard = shell + pads + external;
  const soft = (hard * f.softPct) / 100;
  const contingency = (hard * f.contingencyPct) / 100;
  const letting = (rentRoll * f.lettingPct) / 100;
  const incentives = (rentRoll / 12) * f.rentFreeMonths + g.gla * f.fitOut;
  const total = f.land + hard + soft + contingency + letting + incentives;
  const profit = netValue - total;
  return {
    rentRoll, byKind, noi, gdv, netValue,
    cost: { land: f.land, shell, pads, external, soft, contingency, letting, incentives, total },
    profit,
    margin: netValue ? profit / netValue : 0,
    onCost: total ? profit / total : 0,
    yieldOnCost: total ? (noi / total) * 100 : 0,
    costPerGla: g.gla ? total / g.gla : 0,
  };
}

/** What the plot is worth to this scheme: the land price at which the margin on value comes out at `target`. */
export function retailResidualLand(g: RetailGenerated, f: RetailFinanceInputs, target: number): number {
  const p = retailProForma(g, { ...f, land: 0 });
  return p.netValue * (1 - target) - p.cost.total;
}

/** The same, at a target yield on cost (%): the land price at which NOI / cost equals it. */
export function retailResidualAtYield(g: RetailGenerated, f: RetailFinanceInputs, targetYoc: number): number {
  const p = retailProForma(g, { ...f, land: 0 });
  return targetYoc > 0 ? p.noi / (targetYoc / 100) - p.cost.total : 0;
}

/** Profit as rents move by ±10% (rows) and the exit yield by ±50 basis points (columns). */
export function retailSensitivity(g: RetailGenerated, f: RetailFinanceInputs, rentStep = 0.1, yieldStep = 0.5): number[][] {
  const scale = (k: number) => Object.fromEntries(Object.entries(f.rent).map(([t, v]) => [t, v * k])) as RetailFinanceInputs["rent"];
  return [-1, 0, 1].map((i) => [-1, 0, 1].map((j) => retailProForma(g, { ...f, rent: scale(1 + i * rentStep), exitYield: f.exitYield + j * yieldStep }).profit));
}
